import React from 'react';
import { Button, Box, Typography, Avatar, Stack, Menu, MenuItem, ListItemIcon, ListItemText, SvgIcon, SvgIconProps, CircularProgress } from '@mui/material';
import { createTheme, ThemeProvider } from "@mui/material/styles";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoIcon from '@mui/icons-material/Info';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ProfileModal from '../Profile/Profile';
import SettingsModal from '../Settings/SettingsModal';
import AboutModal from '../About/AboutModal';
import HistoryModal from '../History/HistoryModal';
import DepositCashierModal from './DepositCashierModal';
import AccountProfileModal from './AccountProfileModal';
import { IUserState } from '../../Core/State/IUserState';
import styles from '../../assets/css/components/Header.module.css';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import IconButton from '@mui/material/IconButton';
import { getMuted, setMuted, getVolume, setVolume } from '../../Utils/AudioUtils';
import { appendPokerTokenToUrl, buildHsUrlWithToken } from '../../Utils/SessionUrlUtils';
import Slider from '@mui/material/Slider';
import Popover from '@mui/material/Popover';
import ReplayIcon from '@mui/icons-material/Replay';
import { AppSections } from '../../Core/State/AppSections';
import { buildDepositReturnUrl, createDepositSession } from '../../Casino/CasinoDeposit';
import type { CreateDepositSessionInput, DepositSessionResult } from '../../Casino/Type/CasinoTypes';
import icon from '/images/animated-icon.gif';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import UserTicketsTooltip from './UserTicketsTooltip';
import { IPokerUserSettings } from '../../Core/State/IPokerUserSettings';
import { UserSettingsManager } from '../../Utils/UserSettingsManager';
import LanguageSelector from './LanguageSelector';
import { getCurrentLanguage, t } from '../../i18n';
import { canShowGlobalChat } from '../../Chat/ChatAccessControl';

function LobbyIcon(props: SvgIconProps) {
    return (
        <SvgIcon {...props} viewBox="0 0 12 12">
            <rect width="5.0001" height="5.0001" rx="1"/>
            <rect y="7.00012" width="5.0001" height="5.0001" rx="1"/>
            <rect x="7.00012" width="5.0001" height="5.0001" rx="1"/>
            <rect x="7.00012" y="7.00012" width="5.0001" height="5.0001" rx="1"/>
        </SvgIcon>
    );
}

function PokerIcon(props: SvgIconProps) {
    return (
        <SvgIcon {...props} viewBox="0 0 14 16">
            <path d="M10.6672 12.0006V1.3334C10.6672 0.979758 10.5267 0.640604 10.2766 0.390543C10.0266 0.140483 9.68742 0 9.33378 0L1.3334 0C0.979758 0 0.640603 0.140483 0.390543 0.390543C0.140482 0.640604 0 0.979758 0 1.3334V12.0006C0 12.3542 0.140482 12.6934 0.390543 12.9434C0.640603 13.1935 0.979758 13.334 1.3334 13.334H9.33378C9.68742 13.334 10.0266 13.1935 10.2766 12.9434C10.5267 12.6934 10.6672 12.3542 10.6672 12.0006ZM7.66853 8.62891C7.30509 8.84739 6.88782 8.95996 6.46381 8.95393C6.30849 8.95343 6.15347 8.94028 6.00029 8.91459L6.66699 10.6672H4.00019L4.66689 8.91451C4.05703 9.01502 3.47075 8.92068 2.99864 8.62891C2.3547 8.23098 2.0001 7.53419 2.0001 6.66699C2.0001 5.59944 2.8298 4.90182 3.56184 4.28629C4.02853 3.89394 4.5108 3.48833 4.73689 3.03531C4.79227 2.92459 4.87739 2.83147 4.9827 2.76639C5.08802 2.70131 5.20937 2.66684 5.33317 2.66684C5.45697 2.66684 5.57833 2.70131 5.68364 2.76639C5.78896 2.83147 5.87407 2.92459 5.92945 3.03531C6.15596 3.48833 6.6384 3.89369 7.10492 4.28629C7.83738 4.90182 8.66708 5.59944 8.66708 6.66699C8.66708 7.53419 8.31248 8.23098 7.66853 8.62891Z"/>
            <path d="M11.9983 2.66589H11.3316V11.9997C11.3316 12.5301 11.1209 13.0389 10.7458 13.414C10.3707 13.789 9.862 13.9998 9.33154 13.9998H2.66455V14.6665C2.66455 15.0201 2.80503 15.3593 3.05509 15.6093C3.30515 15.8594 3.64431 15.9999 3.99795 15.9999H11.9983C12.352 15.9999 12.6911 15.8594 12.9412 15.6093C13.1912 15.3593 13.3317 15.0201 13.3317 14.6665V3.99929C13.3317 3.64565 13.1912 3.3065 12.9412 3.05644C12.6911 2.80638 12.352 2.66589 11.9983 2.66589Z"/>
        </SvgIcon>
    );
}

function CasinoIcon(props: SvgIconProps) {
    return (
        <SvgIcon {...props} viewBox="0 0 17 16">
            <path d="M2.35419 7.5579C2.4482 6.27633 2.95341 5.05934 3.79465 4.08797L2.36504 2.65735C1.14834 4.01125 0.430941 5.74033 0.331787 7.5579H2.35419Z"/>
            <path d="M8.77405 0V2.0224C10.0556 2.11642 11.2726 2.62162 12.244 3.46286L13.6738 2.03325C12.3201 0.816716 10.5914 0.0993243 8.77405 0Z"/>
            <path d="M2.35419 8.44226H0.331787C0.430941 10.2598 1.14834 11.9889 2.36504 13.3428L3.79465 11.9122C2.95341 10.9408 2.4482 9.72383 2.35419 8.44226Z"/>
            <path d="M14.3095 8.44226C14.2155 9.72383 13.7103 10.9408 12.869 11.9122L14.2986 13.3421C15.5152 11.9884 16.2326 10.2596 16.3319 8.44226H14.3095Z"/>
            <path d="M12.869 4.08797C13.7103 5.05934 14.2155 6.27633 14.3095 7.5579H16.3319C16.2326 5.74027 15.515 4.01118 14.2981 2.65735L12.869 4.08797Z"/>
            <path d="M4.41976 3.46286C5.39113 2.62162 6.60811 2.11642 7.88968 2.0224V0C6.07212 0.0991543 4.34303 0.816557 2.98914 2.03325L4.41976 3.46286Z"/>
            <path d="M12.244 12.5371C11.2726 13.3784 10.0556 13.8836 8.77405 13.9776V16C10.5917 15.9007 12.3208 15.1831 13.6746 13.9662L12.244 12.5371Z"/>
            <path d="M7.89149 13.9776C6.60992 13.8836 5.39294 13.3784 4.42156 12.5371L2.9917 13.9667C4.3454 15.1833 6.07419 15.9006 7.89149 16V13.9776Z"/>
            <path d="M6.53345 5.47412C6.61081 5.42855 6.70702 5.42763 6.7854 5.47217L10.8303 7.77686C10.9096 7.8224 10.9592 7.90693 10.9592 7.99951C10.9592 8.09218 10.9097 8.17758 10.8303 8.22314L6.7854 10.5269C6.747 10.5489 6.70382 10.56 6.66138 10.5601C6.61738 10.5601 6.57289 10.5479 6.53345 10.5249C6.45573 10.4788 6.40845 10.3943 6.40845 10.3032V5.6958C6.40845 5.6047 6.45563 5.52021 6.53345 5.47412Z"/>
        </SvgIcon>
    );
}

// function BettingIcon(props: SvgIconProps) {
//     return (
//         <SvgIcon {...props} viewBox="0 0 16 16">
//             <path d="M8.65137 1.29243C8.83234 1.05258 8.80324 0.543852 8.77748 0.0950824C8.7765 0.0757386 8.77542 0.0568203 8.77434 0.0376075C8.52068 0.0133868 8.26391 0 8.00402 0C7.0082 0 6.05534 0.183455 5.1759 0.516031C5.80325 0.817513 6.77434 1.33642 7.85373 2.10314C8.22201 1.78621 8.49976 1.49363 8.65137 1.29243Z"/>
//             <path d="M12.7048 5.85735C12.3105 5.9019 11.978 5.96661 11.6936 6.05737C13.0531 8.43106 13.5832 11.4299 13.7129 13.6025C15.0598 12.2302 15.9151 10.375 15.996 8.32C15.068 7.17928 13.8385 5.72875 12.7048 5.85735Z"/>
//             <path d="M4.62259 3.80844C5.57783 3.60967 6.47527 3.12889 7.19083 2.6214C5.69805 1.59327 4.45298 1.05969 4.20531 0.95813C2.65659 1.79535 1.41728 3.12928 0.696289 4.74555C1.63343 4.37216 2.64838 4.18219 3.64525 3.9971C3.96775 3.93721 4.30111 3.87531 4.62259 3.80844Z"/>
//             <path d="M10.1521 4.04105C10.2374 4.12674 10.3224 4.2139 10.4068 4.3027C10.7185 4.62984 11.0012 4.98765 11.2605 5.36622C11.6381 5.22364 12.0827 5.12254 12.6148 5.06238C13.8686 4.92056 14.9835 5.91377 15.9404 7.00723C15.7352 5.34959 15.0243 3.84932 13.965 2.66614C13.3867 2.73582 12.7421 2.85388 12.0851 3.04994C11.6541 3.17867 10.9677 3.52843 10.1521 4.04105Z"/>
//             <path d="M9.09952 8.17244C9.42764 7.06107 9.80771 6.25992 10.5441 5.7399C10.3231 5.42264 10.0853 5.12489 9.82764 4.85441C9.70847 4.72924 9.58796 4.60837 9.46692 4.48962C6.51256 6.50056 2.46565 10.1946 2.09766 13.3952C3.32604 14.7391 5.00715 15.6608 6.89963 15.9223C7.58871 14.5504 8.23726 11.7615 8.62277 10.1028C8.80799 9.3061 8.96784 8.6182 9.09952 8.17244Z"/>
//             <path d="M5.74972 6.41002C6.77982 5.4608 7.86766 4.60961 8.86263 3.92551C8.53098 3.63208 8.1983 3.36159 7.87171 3.11353C7.0453 3.73475 5.96329 4.34662 4.78551 4.59157C4.45569 4.66021 4.11794 4.72296 3.79142 4.78357C2.53332 5.0172 1.32563 5.24347 0.309994 5.81014C0.112203 6.50635 0.00402832 7.24036 0.00402832 7.99998C0.00402832 9.69925 0.535508 11.2737 1.43891 12.5693C2.02416 10.2521 4.0316 7.99323 5.74972 6.41002Z"/>
//             <path d="M9.55169 3.46864C10.5034 2.86188 11.324 2.44233 11.8563 2.28343C12.3344 2.14066 12.8033 2.03667 13.2474 1.96083C12.2212 1.06909 10.9668 0.433526 9.58207 0.156494C9.61277 0.715206 9.62616 1.32861 9.29004 1.77427C9.13035 1.98591 8.86019 2.27616 8.50867 2.59106C8.85293 2.85903 9.20279 3.15125 9.55169 3.46864Z"/>
//             <path d="M9.86663 8.39893C9.74173 8.82207 9.58417 9.49949 9.40189 10.2838C8.86367 12.5997 8.34865 14.6719 7.74719 15.9934C7.83259 15.9961 7.91795 15.9999 8.004 15.9999C9.86843 15.9999 11.5824 15.3604 12.9426 14.2912C12.8888 12.0177 12.3438 8.80194 10.9709 6.41479C10.4385 6.81054 10.1519 7.43311 9.86663 8.39893Z"/>
//         </SvgIcon>
//     );
// }

interface MainProps {
    userConnectedCount: number;
    userState: IUserState;
    logout: () => void;
    toggleProfileModal: () => void;
    setSection: (section: string) => void;
    section: string;
    login?: (username: string, password: string) => void;
    isReconnecting?: boolean;
    uploadAvatar?: (file: File) => Promise<void>;
    onAvatarUpdated?: (data: { userId?: string; avatarUrl: string }) => void;
    updateUserSettings?: (settings: Partial<IPokerUserSettings>) => void;
    updateUserSettingsSilent?: (settings: Partial<IPokerUserSettings>) => void;
    userSettingsManager?: UserSettingsManager;
}

interface HeaderState {
    anchorEl: HTMLElement | null;
    isMuted: boolean;
    volume: number;
    volumeAnchorEl: HTMLElement | null;
    previousBalance: string | null;
    isBalanceUpdated: boolean;
    prevVolumeBeforeMute: number | null;
    isSettingsModalOpen: boolean;
    isAboutModalOpen: boolean;
    isHistoryModalOpen: boolean;
    isAccountProfileModalOpen: boolean;
    isDepositModalOpen: boolean;
    isDepositSessionLoading: boolean;
    isAvatarUploading: boolean;
    depositSession: DepositSessionResult | null;
    depositError: string | null;
}

export default class DesktopHeader extends React.Component<MainProps, HeaderState> {

    private volumeDebounceTimer: NodeJS.Timeout | null = null;
    private volumeMaxWaitTimer: NodeJS.Timeout | null = null;
    private lastVolumeValue: number | null = null;
    private depositSessionAbortController: AbortController | null = null;
    private avatarInputRef = React.createRef<HTMLInputElement>();

    constructor(props: MainProps) {
        super(props);
        this.state = {
            anchorEl: null,
            isMuted: getMuted(),
            volume: getVolume() * 100,
            volumeAnchorEl: null,
            previousBalance: null,
            isBalanceUpdated: false,
            prevVolumeBeforeMute: null,
            isSettingsModalOpen: false,
            isAboutModalOpen: false,
            isHistoryModalOpen: false,
            isAccountProfileModalOpen: false,
            isDepositModalOpen: false,
            isDepositSessionLoading: false,
            isAvatarUploading: false,
            depositSession: null,
            depositError: null
        };
    }

    componentDidMount() {
        // Component mounted
    }

    componentWillUnmount() {
        if (this.volumeDebounceTimer) clearTimeout(this.volumeDebounceTimer);
        if (this.volumeMaxWaitTimer) clearTimeout(this.volumeMaxWaitTimer);
        this.depositSessionAbortController?.abort();
    }

    componentDidUpdate(prevProps: MainProps) {
        if (prevProps.userState.balance !== this.props.userState.balance) {
            this.setState({
                isBalanceUpdated: true,
                previousBalance: prevProps.userState.balance.toString()
            });

            setTimeout(() => {
                this.setState({ isBalanceUpdated: false });
            }, 1000);
        }
    }

    darkTheme = createTheme({
        palette: {
            mode: "dark",
            primary: {
                main: "#F93751"
            }
        },
        typography: {
            fontFamily: 'Inter, serif',
        },
    });

    private handleLogout = async () => {
        this.setState({ anchorEl: null });
        this.props.logout();
    };

    private toggleProfileModal = () => {
        this.setState({ anchorEl: null });
        this.props.toggleProfileModal();
    };

    private buildDepositSessionInput = (): CreateDepositSessionInput | undefined => {
        const input: CreateDepositSessionInput = {};
        const language = getCurrentLanguage();
        const returnUrl = buildDepositReturnUrl();

        if (language) input.language = language;
        if (returnUrl) input.returnUrl = returnUrl;

        return Object.keys(input).length > 0 ? input : undefined;
    };

    private handleDepositSessionClick = async () => {
        if (this.state.isDepositSessionLoading) return;

        this.depositSessionAbortController?.abort();
        const controller = new AbortController();
        this.depositSessionAbortController = controller;

        this.setState({
            isDepositModalOpen: true,
            isDepositSessionLoading: true,
            depositSession: null,
            depositError: null
        });

        try {
            const session = await createDepositSession({
                input: this.buildDepositSessionInput(),
                signal: controller.signal
            });

            if (controller.signal.aborted) return;

            this.setState({
                isDepositSessionLoading: false,
                depositSession: session,
                depositError: session.cashierUrl ? null : t('DesktopHeader:depositFlow.unavailable')
            });
        } catch (err) {
            if (controller.signal.aborted) return;

            this.setState({
                isDepositSessionLoading: false,
                depositSession: null,
                depositError: err instanceof Error && err.message ? err.message : t('DesktopHeader:depositFlow.error')
            });
        } finally {
            if (this.depositSessionAbortController === controller) {
                this.depositSessionAbortController = null;
            }
        }
    };

    private handleDepositModalClose = () => {
        this.depositSessionAbortController?.abort();
        this.depositSessionAbortController = null;
        this.setState({
            isDepositModalOpen: false,
            isDepositSessionLoading: false,
            depositSession: null,
            depositError: null
        });
    };

    private handleAccountMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        this.setState({ anchorEl: event.currentTarget });
    };

    private handleAccountMenuClose = () => {
        this.setState({ anchorEl: null });
    };

    private handleAvatarMenuClick = () => {
        if (!this.props.uploadAvatar || this.state.isAvatarUploading) return;

        this.setState({ anchorEl: null }, () => {
            this.avatarInputRef.current?.click();
        });
    };

    private handleAvatarFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file || !this.props.uploadAvatar) return;

        this.setState({ isAvatarUploading: true });

        try {
            await this.props.uploadAvatar(file);
        } catch {
            // The upload handler owns user-facing error notifications.
        } finally {
            this.setState({ isAvatarUploading: false });
        }
    };

    private handleMuteToggle = () => {
        this.setState(prevState => {
            const newMutedState = !prevState.isMuted;
            if (newMutedState) {
                const remembered = prevState.volume;
                setMuted(true);
                setVolume(0);

                this.lastVolumeValue = 0;
                if (this.volumeDebounceTimer) clearTimeout(this.volumeDebounceTimer);
                if (this.volumeMaxWaitTimer) clearTimeout(this.volumeMaxWaitTimer);

                this.volumeDebounceTimer = setTimeout(() => {
                    this.sendVolumeUpdate();
                }, 500);

                return { isMuted: true, prevVolumeBeforeMute: remembered, volume: 0 } as Partial<HeaderState> as HeaderState;
            } else {
                const restore = (prevState.prevVolumeBeforeMute ?? 100);
                setMuted(false);
                setVolume(restore / 100);

                this.lastVolumeValue = Math.round((restore / 100) * 10) / 10;
                if (this.volumeDebounceTimer) clearTimeout(this.volumeDebounceTimer);
                if (this.volumeMaxWaitTimer) clearTimeout(this.volumeMaxWaitTimer);

                this.volumeDebounceTimer = setTimeout(() => {
                    this.sendVolumeUpdate();
                }, 500);

                return { isMuted: false, prevVolumeBeforeMute: null, volume: restore } as Partial<HeaderState> as HeaderState;
            }
        });
    };

    private handleVolumeIconClick = (event: React.MouseEvent<HTMLElement>) => {
        this.setState({ volumeAnchorEl: event.currentTarget });
    };

    private handleVolumeClose = () => {
        this.setState({ volumeAnchorEl: null });
    };

    private handleVolumeChange = (_: Event, newValue: number | number[]) => {
        const newVolume = Array.isArray(newValue) ? newValue[0] : newValue;
        const normalizedVolume = Math.round((newVolume / 100) * 10) / 10;

        setVolume(newVolume / 100);
        const willMute = newVolume === 0;
        setMuted(willMute);
        this.setState(prev => ({
            volume: newVolume,
            isMuted: willMute,
            prevVolumeBeforeMute: willMute ? (prev.prevVolumeBeforeMute ?? prev.volume) : null
        }));

        this.lastVolumeValue = normalizedVolume;

        if (this.volumeDebounceTimer) {
            clearTimeout(this.volumeDebounceTimer);
        }

        if (!this.volumeMaxWaitTimer) {
            this.volumeMaxWaitTimer = setTimeout(() => {
                this.sendVolumeUpdate();
            }, 2000);
        }

        this.volumeDebounceTimer = setTimeout(() => {
            this.sendVolumeUpdate();
        }, 500);
    };

    private sendVolumeUpdate = () => {
        if (this.lastVolumeValue !== null && this.props.updateUserSettingsSilent) {
            this.props.updateUserSettingsSilent({ clientVolume: this.lastVolumeValue });
            this.lastVolumeValue = null;
        }

        if (this.volumeDebounceTimer) {
            clearTimeout(this.volumeDebounceTimer);
            this.volumeDebounceTimer = null;
        }

        if (this.volumeMaxWaitTimer) {
            clearTimeout(this.volumeMaxWaitTimer);
            this.volumeMaxWaitTimer = null;
        }
    };

    private handleSectionChange = (newSection: string) => {
        const { section, setSection } = this.props;

        if (section !== newSection) {
            setSection(newSection);
        }
    };

    private handleReloadApp = () => {
        this.setState({ anchorEl: null });
        window.location.reload();
    };


    private openExternalWithRestore = (baseUrl: string) => {
        const url = appendPokerTokenToUrl(baseUrl);
        window.open(url, '_blank');
    };

    private handleCasinoClick = () => {
        return;
    };

    private handleSettingsClick = () => {
        this.setState({ anchorEl: null, isSettingsModalOpen: true });
    };

    private handleSettingsModalClose = () => {
        this.setState({ isSettingsModalOpen: false });
    };

    private handleAboutClick = () => {
        this.setState({ anchorEl: null, isAboutModalOpen: true });
    };

    private handleAboutModalClose = () => {
        this.setState({ isAboutModalOpen: false });
    };

    private handleHistoryClick = () => {
        this.setState({ anchorEl: null, isHistoryModalOpen: true });
    };

    private handleHistoryModalClose = () => {
        this.setState({ isHistoryModalOpen: false });
    };

    private handleAccountProfileClick = () => {
        this.setState({ anchorEl: null, isAccountProfileModalOpen: true });
    };

    private handleAccountProfileModalClose = () => {
        this.setState({ isAccountProfileModalOpen: false });
    };

    private handleUpdateUserSettings = (settings: Partial<IPokerUserSettings>) => {
        if (this.props.updateUserSettings) {
            this.props.updateUserSettings(settings);
        }
    };

    render() {
        const user = this.props.userState;
        const balance = user.balance ? Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(user.balance) : '0.00';
        const { anchorEl } = this.state;
        const canUseEmbeddedAccountProfile = canShowGlobalChat(user.username);

        return (
            <ThemeProvider theme={this.darkTheme}>
                <header className={styles.header}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row">
                            <Stack direction="row" alignItems="center" mr={2}>
                                <img src={icon} alt='PokerSolutions Icon' height={44} />
                            </Stack>
                            <Stack direction="row" alignItems="center" className={this.props.section === AppSections.LOBBY_HOME ? styles.navItemActive : styles.navItem} onClick={() => this.handleSectionChange(AppSections.LOBBY_HOME)} style={{ marginRight: '2rem' }}>
                                <LobbyIcon className={styles.navItemIcon} sx={{ height: 12, width: 12, marginRight: '0.5rem' }} />
                                <Typography className={styles.navItemText} sx={{ margin: 0, fontSize: '14px', fontWeight: 600}}>{t('DesktopHeader:nav.lobby')}</Typography>
                            </Stack>
                            <Stack
                                direction="row"
                                alignItems="center"
                                className={(this.props.section !== AppSections.LOBBY_HOME && this.props.section !== AppSections.CASINO && this.props.section !== AppSections.CASINO_GAME_VIEW) ? styles.navItemActive : styles.navItem}
                                onClick={() => this.handleSectionChange(AppSections.LOBBY_AOF_FIAT)}
                                style={{ marginRight: '2rem' }}
                            >
                                <PokerIcon className={styles.navItemIcon} sx={{ height: 16, width: 14, marginRight: '0.5rem' }} />
                                <Typography className={styles.navItemText} sx={{ margin: 0, fontSize: '14px', fontWeight: 600}}>{t('DesktopHeader:nav.poker')}</Typography>
                            </Stack>
                            <Stack
                                direction="row"
                                alignItems="center"
                                className={(this.props.section === AppSections.CASINO || this.props.section === AppSections.CASINO_GAME_VIEW) ? styles.navItemActive : styles.navItem}
                                onClick={this.handleCasinoClick}
                                style={{ marginRight: '2rem' }}
                            >
                                <CasinoIcon className={styles.navItemIcon} sx={{ width: 16, height: 16, marginRight: '0.5rem' }} />
                                <Typography className={styles.navItemText} sx={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>{t('DesktopHeader:nav.casino')}</Typography>
                            </Stack>
                            {/* <Stack direction="row" alignItems="center" className={styles.navItem} onClick={() => this.openExternalWithRestore(buildHsUrlWithToken('/betting'))} style={{ marginRight: '2rem' }}>
                                <BettingIcon className={styles.navItemIcon} sx={{ width: 16, height: 16, marginRight: '0.5rem' }} />
                                <Typography className={styles.navItemText} sx={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>Betting</Typography>
                            </Stack> */}
                        </Stack>
                        <Stack direction="row" alignItems="center" gap="2rem">
                            {user.authenticated && (
                                <Stack direction="row" alignItems="center" gap="1rem">
                                    <Typography
                                        className={`${styles.balance} ${this.state.isBalanceUpdated ? styles.balanceUpdated : ''}`}
                                        component="p"
                                        variant="body1"
                                        style={{ fontWeight: 600 }}
                                    >
                                        ${balance}
                                    </Typography>
                                    <UserTicketsTooltip tickets={user.tickets}>
                                        <span>
                                            <IconButton
                                                color={Array.isArray(user.tickets) && user.tickets.length > 0 ? 'primary' : 'default'}
                                                size="small"
                                                disabled={!Array.isArray(user.tickets) || user.tickets.length === 0}
                                                sx={{
                                                    backgroundColor: (Array.isArray(user.tickets) && user.tickets.length > 0) ? 'rgba(249, 55, 81,0.08)' : 'transparent',
                                                    border: '1px solid',
                                                    borderColor: (Array.isArray(user.tickets) && user.tickets.length > 0) ? 'rgba(249, 55, 81,0.3)' : 'rgba(255,255,255,0.12)'
                                                }}
                                            >
                                                <ConfirmationNumberIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </UserTicketsTooltip>
                                    <Button
                                        variant="contained"
                                        disabled={this.state.isDepositSessionLoading}
                                        style={{ padding: '6px 28px', fontSize: '14px', borderRadius: '6px', fontWeight: 800, textTransform: 'none', height: '36px' }}
                                        onClick={this.handleDepositSessionClick}
                                    >
                                        {this.state.isDepositSessionLoading ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            'Deposit'
                                        )}
                                    </Button>
                                    <Stack direction="row" spacing={1} alignItems="center" onClick={this.handleAccountMenuClick} style={{ cursor: "pointer" }}>
                                        <Avatar alt="User avatar" src={this.props.userState.avatarUrl || undefined} sx={{ width: 30, height: 30 }} />
                                        <Typography className={styles.username} component="p" variant="body1">{user.username}</Typography>
                                    </Stack>
                                    <input
                                        ref={this.avatarInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/bmp,image/gif"
                                        style={{ display: 'none' }}
                                        onChange={this.handleAvatarFileChange}
                                    />
                                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={this.handleAccountMenuClose}>
                                        <MenuItem
                                            onClick={this.handleAvatarMenuClick}
                                            disabled={!this.props.uploadAvatar || this.state.isAvatarUploading}
                                        >
                                            <ListItemIcon>
                                                {this.state.isAvatarUploading ? (
                                                    <CircularProgress size={18} color="inherit" />
                                                ) : (
                                                    <PhotoCameraIcon fontSize="small" />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText>Change avatar</ListItemText>
                                        </MenuItem>
                                        {canUseEmbeddedAccountProfile ? (
                                            <MenuItem onClick={this.handleAccountProfileClick}>
                                                <ListItemIcon>
                                                    <AccountCircleIcon fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText>{t('DesktopHeader:menu.myAccount')}</ListItemText>
                                            </MenuItem>
                                        ) : (
                                            <MenuItem
                                                component="a"
                                                href={buildHsUrlWithToken('/account/profile')}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={this.handleAccountMenuClose}
                                            >
                                                <ListItemIcon>
                                                    <AccountCircleIcon fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText>{t('DesktopHeader:menu.myAccount')}</ListItemText>
                                            </MenuItem>
                                        )}
                                        <MenuItem onClick={this.handleHistoryClick}>
                                            <ListItemIcon>
                                                <ReceiptLongIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>{t('DesktopHeader:menu.history')}</ListItemText>
                                        </MenuItem>
                                        <MenuItem onClick={this.handleSettingsClick}>
                                            <ListItemIcon>
                                                <SettingsIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>{t('DesktopHeader:menu.settings')}</ListItemText>
                                        </MenuItem>
                                        <MenuItem onClick={this.handleAboutClick}>
                                            <ListItemIcon>
                                                <InfoIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>{t('DesktopHeader:menu.about')}</ListItemText>
                                        </MenuItem>
                                        <MenuItem onClick={this.handleReloadApp}>
                                            <ListItemIcon>
                                                <ReplayIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>{t('DesktopHeader:menu.reloadApp')}</ListItemText>
                                        </MenuItem>
                                        <MenuItem onClick={this.handleLogout}>
                                            <ListItemIcon>
                                                <LogoutIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>{t('DesktopHeader:menu.logout')}</ListItemText>
                                        </MenuItem>
                                    </Menu>
                                    <LanguageSelector variant="icon" userSettingsManager={this.props.userSettingsManager} />
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <IconButton
                                            onClick={this.handleVolumeIconClick}
                                            color="inherit"
                                            size="small"
                                        >
                                            {this.state.isMuted || this.state.volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
                                        </IconButton>
                                        <Popover
                                            open={Boolean(this.state.volumeAnchorEl)}
                                            anchorEl={this.state.volumeAnchorEl}
                                            onClose={this.handleVolumeClose}
                                            anchorOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'center',
                                            }}
                                            transformOrigin={{
                                                vertical: 'top',
                                                horizontal: 'center',
                                            }}
                                        >
                                            <Box sx={{
                                                py: 2,
                                                px: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: 120,
                                                width: 40,
                                                bgcolor: 'background.paper'
                                            }}>
                                                <Slider
                                                    size="small"
                                                    value={this.state.volume}
                                                    onChange={this.handleVolumeChange}
                                                    aria-label="Volume"
                                                    orientation="vertical"
                                                    sx={{
                                                        '& .MuiSlider-thumb': {
                                                            width: 12,
                                                            height: 12,
                                                        },
                                                        height: 100
                                                    }}
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={this.handleMuteToggle}
                                                    aria-label={this.state.isMuted ? 'Unmute' : 'Mute'}
                                                    sx={{ mt: 0.5 }}
                                                >
                                                    {this.state.isMuted ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
                                                </IconButton>
                                            </Box>
                                        </Popover>
                                    </Box>
                                </Stack>
                            )}
                        </Stack>
                    </Stack>
                    {this.props.userState.isProfileOpen && (
                        <ProfileModal open={this.props.userState.isProfileOpen} handleClose={this.toggleProfileModal} userState={this.props.userState} />
                    )}
                    {this.props.userSettingsManager && (
                    <SettingsModal
                        open={this.state.isSettingsModalOpen}
                        handleClose={this.handleSettingsModalClose}
                        userState={this.props.userState}
                        updateUserSettings={this.handleUpdateUserSettings}
                        userSettingsManager={this.props.userSettingsManager}
                    />
                )}
                <HistoryModal
                    open={this.state.isHistoryModalOpen}
                    handleClose={this.handleHistoryModalClose}
                    userState={this.props.userState}
                />
                <AccountProfileModal
                    open={this.state.isAccountProfileModalOpen}
                    onClose={this.handleAccountProfileModalClose}
                    onAvatarUpdated={this.props.onAvatarUpdated}
                />
                <AboutModal
                    open={this.state.isAboutModalOpen}
                    handleClose={this.handleAboutModalClose}
                />
                <DepositCashierModal
                    open={this.state.isDepositModalOpen}
                    loading={this.state.isDepositSessionLoading}
                    error={this.state.depositError}
                    session={this.state.depositSession}
                    onClose={this.handleDepositModalClose}/>
                </header>
            </ThemeProvider>
        );
    }
};
